import { Component, Inject, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { AuthorizedUser, BusyService, ConfigStorage, getFormErrorList, requiredIfValidator, UserStorage } from "@scisys-uim/uim-common";
import { AssetsUser, Firearm, FIREARM_STORAGE_LOCATION_METADATA, FIREARM_STORAGE_LOCATION_TYPE, FIREARM_TYPE, FIREARM_TYPE_MODERATOR, FirearmAssignment, FirearmAssignmentType, FirearmStorageLocation } from "assets-model";
import { FirearmAssignablePersonService, FirearmAssignmentActivationData, FirearmAssignmentService, FirearmDocumentService, FirearmStorageLocationService, TextSectionService } from "assets-service";
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from "rxjs";
import { filter, tap } from "rxjs/operators";


export interface FirearmAssignmentActivationDialogData {
  notifyPolice?: boolean;
  policeNotificationReason?: string;
  firearmAssignment?: FirearmAssignment;
  location?: FirearmStorageLocation;
  gunDealerLocation?: FirearmStorageLocation;
  acquisitionDocumentsPresent?: boolean;
}

export interface FirearmAssignmentActivationDialogConfigData {
  firearmAssignment?: FirearmAssignment;
}

@Component({
  selector: 'app-firearm-assignment-activation-dialog',
  templateUrl: './firearm-assignment-activation-dialog.component.html',
  styleUrls: ['./firearm-assignment-activation-dialog.component.scss']
})
export class FirearmAssignmentActivationDialogComponent implements OnInit {

  protected systemConfig: any;

  public form: FormGroup;

  public item: FirearmAssignmentActivationDialogData = {
  };

  protected userDetails: AuthorizedUser;

  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  get firearmAssetTypeName(): string {
    const firearm = this.data?.firearmAssignment?.firearm;
    if (firearm != null) {
      if (firearm?.firearmType == FIREARM_TYPE_MODERATOR) {
        return 'Moderator';
      } else {
        return 'Firearm';
      }
    }
    return "Firearm Asset";
  }

  get busy() {
    return !!this.busyService?.isBusy();
  }
  get busy$(): Observable<boolean> {
    return this.busyService?.busy$;
  }

  get policeNotificationReasonText(): string {
    if (!this.item.notifyPolice) {
      return null;
    }
    return this.item?.policeNotificationReason;
  }
  set policeNotificationReasonText(v: string) {
    if (!this.item) {
      return;
    }
    if (!this.item.notifyPolice) {
      return;
    }
    this.item.policeNotificationReason = v;
  }

  get firearmTypeName(): string {
    return FIREARM_TYPE.typeName(this.data?.firearmAssignment?.firearm);
  }

  private _onInitCompleteSubject: Subject<boolean> = new BehaviorSubject<boolean>(null);
  $onInitComplete: Observable<boolean> = this._onInitCompleteSubject.asObservable().pipe(filter(o => o != null));
  private onInitSubscription: Subscription = null;

  constructor(
    private dialogRef: MatDialogRef<FirearmAssignmentActivationDialogComponent, FirearmAssignmentActivationDialogData>,
    @Inject(MAT_DIALOG_DATA) public data: FirearmAssignmentActivationDialogConfigData,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private firearmAssignablePersonService: FirearmAssignablePersonService,
    private firearmDocumentService: FirearmDocumentService,
    private firearmStorageLocationService: FirearmStorageLocationService,
    private textSectionService: TextSectionService,
    private firearmAssigmentService: FirearmAssignmentService,
    private busyService: BusyService,
    protected configService: ConfigStorage,
    protected userStorage: UserStorage) {

    dialogRef.disableClose = true;

    this.data = this.data || {
    };

    this.form = this.fb.group({
      notifyPolice: [null, []],
      policeNotificationReason: [null, []],
      location: [null, []],
      gunDealerLocation: [null, []],
    }, {
      validators: [
        requiredIfValidator(() => this.policeNotificationReason, () => this.isPoliceNotificationReasonRequired(), "Police Notification Reason", { required: "Police Notification Reason is required if Notify Police is selected" })
        , requiredIfValidator(() => this.location, () => this.isConfirmationLocationRequired(), "Location", { required: "Location is required to confirm this assignment" })
        , requiredIfValidator(() => this.gunDealerLocation, () => this.isAcquisition(), "Gun Dealer", { required: "Gun Dealer is required to confirm receipt" })
      ]
    });

    this.item.firearmAssignment = this.data.firearmAssignment;
    this.item.location = this.data.firearmAssignment?.location;
    this.item.gunDealerLocation = this.data.firearmAssignment?.gunDealerLocation;
    this.item.notifyPolice = this.isPoliceNotificationDefault();

    if (this.isPoliceNotificationAvailable()) {
      var sectionId = null;

      if (this.data?.firearmAssignment.firearmAssignmentType == 'ISSUE') {
        sectionId = 'firearms.assignment.issue.policeNotificationReason';
      } else {
        if (this.data?.firearmAssignment?.storageType == "ACQUIRE") {
          sectionId = 'firearms.assignment.storage.acquire.policeNotificationReason';
        }
        if (this.data?.firearmAssignment?.storageType == "DISPOSE") {
          sectionId = 'firearms.assignment.storage.dispose.policeNotificationReason';
        }
        if (this.data?.firearmAssignment?.storageType == "RETURN") {
          sectionId = 'firearms.assignment.storage.return.policeNotificationReason';
        }
        if (this.data?.firearmAssignment?.storageType == "TRANSFER") {
          sectionId = 'firearms.assignment.storage.transfer.policeNotificationReason';
        }
      }

      if (sectionId != null) {
        textSectionService.get(sectionId).subscribe(textSection => {
          this.item.policeNotificationReason = textSection?.text;
        }, error => {
          // suppress - default text allowed to not exist
        });
      }
    }

    this.setDefaultDisabledFields();
  }

  locationSelectionList: FirearmStorageLocation[] = [];
  gunDealerLocationSelectionList: FirearmStorageLocation[] = [];

  protected onInitAsyncComplete() {
    if (this.isAcquisition() || this.isDisposal()) {
      this.firearmStorageLocationService.findByFullFilters({
        includeNational: true,
        buId: this.data.firearmAssignment?.businessUnit?.buId,
        locationType: this.isAcquisition() ? 'F' : null,
        disposalLocation: this.isDisposal() ? true : null
      }).subscribe(result => this.locationSelectionList = result.items || []);
    }
    if (this.isAcquisition()) {
      this.firearmStorageLocationService.findByFullFilters({ locationType: 'G' })
        .subscribe(result => this.gunDealerLocationSelectionList = result.items || []);
      this.firearmDocumentService.countFirearmDocuments(this.data.firearmAssignment?.firearm?.assetId, 'ACQUISITION')
        .subscribe(count => this.item.acquisitionDocumentsPresent = count > 0);
    }
  }

  ngAfterViewInit() {
  }

  isIssue() {
    return this.item?.firearmAssignment?.firearmAssignmentType == 'ISSUE';
  }

  isPoolFirearmIssue() {
    if (this.isIssue()) {
      if (this.item?.firearmAssignment?.firearm?.poolFirearm) {
        return true;
      };
    }
    return false;
  }

  isPersonalFirearmIssue() {
    if (this.isIssue()) {
      if (this.item?.firearmAssignment?.personalFirearmAssignment) {
        return true;
      };
    }
    return false;
  }

  isDisposal() {
    return this.isStorageType('DISPOSE');
  }

  isStorageType(storageType: string) {
    if (this.item?.firearmAssignment?.firearmAssignmentType == 'STORAGE') {
      if (this.item?.firearmAssignment?.storageType == storageType) {
        return true;
      }
    }
    return false;
  }

  isModerator() {
    return this.item?.firearmAssignment?.firearm?.firearmType == FIREARM_TYPE_MODERATOR;
  }

  isAcquisition() {
    return this.isStorageType('ACQUIRE');
  }

  isTransfer() {
    return this.isStorageType('TRANSFER');
  }

  isReturn() {
    return this.isStorageType('RETURN');
  }

  isPoliceNotificationDefault(): boolean {
    if (!this.isPoliceNotificationAvailable() || this.isIssue() || this.isReturn() || this.isTransfer()) {
      return false;
    }
    if (this.isPoliceNotificationMandatory()) {
      return true;
    }
    return false;
  }

  isPoliceNotificationMandatory(): boolean {
    return this.isAcquisition() || this.isDisposal();
  }

  isConfirmationLocationRequired(): boolean {
    return this.isAcquisition() || this.isDisposal();
  }

  locationDisplay(location: FirearmStorageLocation): string {
    return location == null ? '' : FIREARM_STORAGE_LOCATION_TYPE.description(location);
  }

  locationCompare(a: any, b: any) {
    return FIREARM_STORAGE_LOCATION_METADATA.equals(a, b);
  }

  get location() {
    return this.form?.get('location');
  }

  get gunDealerLocation() {
    return this.form?.get('gunDealerLocation');
  }

  isPoliceNotificationReasonRequired(): boolean {
    return this.isPoliceNotificationAvailable() && this.item?.notifyPolice;
  }

  isPoliceNotificationAvailable(): boolean {
    if (this.isPersonalFirearmIssue() || this.isModerator()) {
      return false;
    }
    return true;
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }
    if (this.isPoliceNotificationMandatory()) {
      this.notifyPolice.disable();
    }
  }

  getFormErrorList() {
    var result = [...getFormErrorList(this.form)];
    return result;
  }

  closeDialog() {
    this.dialogRef.close();
  }

  isActivateEnabled() {
    return !this.form.invalid && !this.busy;
  }

  activateFirearmAssignment() {
    const serviceParam: FirearmAssignmentActivationData = {
      firearmAssignmentId: this.item?.firearmAssignment?.firearmAssignmentId,
      notifyPolice: this.item?.notifyPolice,
      policeNotificationReason: this.item?.notifyPolice ? this.item?.policeNotificationReason : null,
      locationId: this.item?.location?.firearmStorageLocationId,
      gunDealerLocationId: this.item?.gunDealerLocation?.firearmStorageLocationId
    };

    this.firearmAssigmentService.activateAssignment(serviceParam).subscribe(firearmAssignment => {
      this.item.firearmAssignment = firearmAssignment;
      this.dialogRef.close(this.item);
    });
  }

  firearmDisplay(item: Firearm) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return FIREARM_TYPE.description(item);
  }

  get firearm() {
    return this.control('firearm');
  }

  // policeNotificationReason field
  notifyPoliceChanging(value: boolean, unsetDependents: boolean = true) { }

  notifyPoliceChanged(value: boolean, unsetDependents: boolean = true) {
    this.policeNotificationReason.updateValueAndValidity();
  }

  get notifyPolice() {
    return this.control('notifyPolice');
  }

  // policeNotificationReason field
  policeNotificationReasonChanging(value: string, unsetDependents: boolean = true) { }

  policeNotificationReasonChanged(value: string, unsetDependents: boolean = true) {
  }

  get policeNotificationReason() {
    return this.control('policeNotificationReason');
  }

  // form controls general

  control(name: string) {
    return this.form ? this.form.get(name) : null;
  }

  // 'framework'

  ngOnInit() {
    this.onInitAsync();
  }


  protected onInitAsync() {
    const allObservables: Observable<any>[] = this.onInitAsyncObservables() || [];
    this.onInitSubscription = combineLatest(allObservables).subscribe(() => {
      this.onInitSubscription?.unsubscribe();
      this._onInitAsyncComplete();
    });
  }

  protected _onInitAsyncComplete() {
    this.onInitAsyncComplete();
    this.configService.itemChanges().subscribe((configData: any) => this.initConfig(configData));
    this._onInitCompleteSubject.next(true);
    this._onInitCompleteSubject.complete();
  }

  // These observables will be waited for, on page load, before the screen is ready to search.
  // These Observables are required by the framework - take care in overriding
  protected onInitAsyncObservables(): Observable<any>[] {
    return [this.onInitConfigObservable(),
    this.onInitUserDetails()];
  }
  protected onInitConfigObservable(): Observable<any> {
    return this.configService.resolve().pipe(tap((configData: any) => this.initConfig(configData)));
  }
  protected initConfig(config: any) {
    this.systemConfig = config || {};
  }
  protected onInitUserDetails(): Observable<AuthorizedUser> {
    return this.userStorage.nextNonNullItem().pipe(tap((userDetails: AuthorizedUser) => this.initUserDetails(userDetails)));
  }
  protected initUserDetails(userDetails: AuthorizedUser) {
    this.userDetails = userDetails;
  }


  disabledTooltip(element) {
    return element && (element.valid || !element.touched || !element.value);
  }
}
